import React, { useState } from "react";
import { api, upload } from "./api";
import { eastern } from "./RecordScreen";
import { MedStatusBadge } from "./MedStatusBadge";
import { useProblems, FieldProblem, invalid } from "./form-check";
import { addFormRules } from "./checks";

/**
 * Medications NOT on the application, added at the med station (build plan
 * §9.1 #4): their own "Added at the med station" section, never mixed into
 * the parent's table. Adding one never blocks the counter: it is saved held,
 * pending authorization. At least one label photo (the phone's camera, or a
 * chosen file on a desktop) and the attestation come first.
 */

export interface DocView {
  docKey: string;
  docType: string;
  fileName: string;
  relatedKey: string;
  uploadedByEmail: string;
  uploadedAtUtc: string;
  captureMethod: string;
}

export interface Label {
  name: string;
  strength: string;
  form: string;
  dose: string;
  frequency: string;
  windows: string[];
  route: string;
  isPrn: boolean;
  prnInstructions: string;
  controlled: boolean;
  prescriber: string;
  pharmacy: string;
  rxNumber: string;
}

export interface AttestationView {
  name: string;
  relationship: string;
  phone: string;
  text: string;
  atUtc: string;
  witnessEmail: string;
  witnessName: string;
  /** The three questions (absent on an attestation recorded before them). */
  statement?: StatementView;
}

export interface StatementView {
  started: string;
  reaction: string;
  reactionDetail: string;
  newAllergy: string;
  newAllergyDetail: string;
}

export const STARTED_LABEL: Record<string, string> = { New: "New (started within the last 30 days)", Established: "Established", NotSure: "Not sure" };
const FLAG_LABEL: Record<string, string> = { New: "New", Reaction: "Reaction", Allergy: "Allergy" };

/** The pending queue's flags as badges: raise these first on the Teams call. */
export function StatementFlags({ flags }: { flags: string[] }) {
  if (!flags.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {flags.map((f) => <span key={f} className="rounded bg-red-700 px-1.5 py-0.5 text-xs font-semibold text-white">{FLAG_LABEL[f] ?? f}</span>)}
    </span>
  );
}

/** "Parent's statement at check-in": read-only, for the authorization screens. */
export function StatementBlock({ a }: { a: AttestationView | null }) {
  if (!a) return null;
  const s = a.statement;
  return (
    <div className="mt-2 rounded-md border border-slate-300 bg-slate-50 p-2 text-sm">
      <p className="font-semibold">{a.relationship === "Self" ? "Attendee's statement at check-in" : "Parent's statement at check-in"}</p>
      <p className="text-xs text-slate-500">{a.name}{a.relationship !== "Self" ? ` (${a.relationship.toLowerCase()})` : ""} · {eastern(a.atUtc)}</p>
      {s ? (
        <dl className="mt-1 space-y-0.5">
          <div><dt className="inline font-medium">How long taking it: </dt><dd className={`inline ${s.started === "New" ? "font-semibold text-red-800" : ""}`}>{STARTED_LABEL[s.started] ?? s.started}</dd></div>
          <div><dt className="inline font-medium">Any reaction, or problem with other medications: </dt><dd className={`inline ${s.reaction === "Yes" ? "font-semibold text-red-800" : ""}`}>{s.reaction === "Yes" ? `Yes: ${s.reactionDetail}` : "No"}</dd></div>
          <div><dt className="inline font-medium">Medication allergies not on the application: </dt><dd className={`inline ${s.newAllergy === "Yes" ? "font-semibold text-red-800" : ""}`}>{s.newAllergy === "Yes" ? `Yes: ${s.newAllergyDetail}` : "No"}</dd></div>
        </dl>
      ) : (
        <p className="text-slate-600">Not asked (recorded before the three questions).</p>
      )}
    </div>
  );
}

export interface AddedMed {
  med: { medKey: string; receivedStatus: string; receivedQuantity: string; receivedByEmail: string; receivedAtUtc: string; countVerifiedByEmail: string; controlled: boolean; authorizationStatus: string; status: string; confirmedName: string; confirmedDose: string; confirmedRoute: string; confirmedWindows: string[]; isPrn: string; prnInstructions: string };
  source: string;
  label: Label | null;
  attestation: AttestationView | null;
  photos: DocView[];
  authorizations: DocView[];
  authorizationPath: string;
}

export interface AddedContext {
  personKey: string;
  adult: boolean;
  windows: { code: string; name: string; startTime: string; endTime: string }[];
  routes: string[];
  exceptionCodes: string[];
  attestationText: string;
  selfAttestationText: string;
  addedSource: string;
  canReceive: boolean;
}

const SOURCE_LABEL: Record<string, string> = { AddedAtCheckin: "Added at check-in", AddedMidweek: "Added mid-week" };


export function Thumb({ doc }: { doc: DocView }) {
  const src = `/api/med-documents/${encodeURIComponent(doc.docKey)}`;
  return (
    <a href={src} target="_blank" rel="noreferrer" className="block h-20 w-20 overflow-hidden rounded border border-slate-200 bg-slate-50" title={doc.fileName}>
      <img src={src} alt="Label photo" className="h-full w-full object-cover" loading="lazy" />
    </a>
  );
}

export function AddedSection<V>({ added, ctx, onDone }: { added: AddedMed[]; ctx: AddedContext; onDone: (v: V) => void }) {
  const [adding, setAdding] = useState(false);
  return (
    <section className="section-card">
      <h3 className="mb-2 text-base font-semibold">Added at the med station ({added.length})</h3>
      <p className="mb-2 text-xs text-slate-500">Medications the family brought that are not on the application. Each is held, not given, until a provider authorizes it.</p>
      {added.length > 0 && (
        <ul className="space-y-3">
          {added.map((a) => (
            <li key={a.med.medKey} className="rounded-md border border-slate-200 p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="min-w-0 break-words font-medium">{a.label ? [a.label.name, a.label.strength, a.label.form].filter(Boolean).join(" ") : a.med.confirmedName}</p>
                <MedStatusBadge m={a.med} />
              </div>
              {a.label && (
                <p className="text-sm text-slate-700">
                  Label: {a.label.dose} · {a.label.route} · {a.label.isPrn ? `as needed: ${a.label.prnInstructions}` : a.label.frequency}
                  {a.label.controlled && " · controlled"}
                  {(a.label.prescriber || a.label.pharmacy || a.label.rxNumber) && <span className="block text-xs text-slate-500">{[a.label.prescriber && `Prescriber: ${a.label.prescriber}`, a.label.pharmacy && `Pharmacy: ${a.label.pharmacy}`, a.label.rxNumber && `Rx ${a.label.rxNumber}`].filter(Boolean).join(" · ")}</span>}
                </p>
              )}
              <p className="text-xs text-slate-500">
                {SOURCE_LABEL[a.source] ?? a.source} · {a.med.receivedQuantity ? `${a.med.receivedQuantity} counted · ` : ""}by {a.med.receivedByEmail} · {eastern(a.med.receivedAtUtc)}
                {a.med.countVerifiedByEmail && ` · count verified by ${a.med.countVerifiedByEmail}`}
              </p>
              {a.attestation && (
                <p className="text-xs text-slate-600">
                  Attested by {a.attestation.relationship === "Self" ? `the attendee (${a.attestation.name})` : `${a.attestation.name} (${a.attestation.relationship.toLowerCase()})`}, {eastern(a.attestation.atUtc)}, witnessed by {a.attestation.witnessName || a.attestation.witnessEmail}.
                </p>
              )}
              {a.med.authorizationStatus === "Authorized" && (
                <p className="mt-1 text-sm text-emerald-900">Authorized: {a.med.confirmedDose} · {a.med.confirmedRoute} · {a.med.isPrn === "Y" ? `as needed: ${a.med.prnInstructions}` : `windows ${a.med.confirmedWindows.join(", ")}`}</p>
              )}
              <div className="mt-2 flex flex-wrap gap-2">{a.photos.map((p) => <Thumb key={p.docKey} doc={p} />)}</div>
              <p className="mt-2 text-sm"><a className="text-program underline" href={`/authorizations/${encodeURIComponent(a.med.medKey)}`}>{a.med.authorizationStatus === "Pending" ? "Authorization (Path A on site · Path B remote)" : "Authorization details"}</a></p>
            </li>
          ))}
        </ul>
      )}
      {ctx.canReceive && !adding && <button type="button" className="btn-secondary mt-3" onClick={() => setAdding(true)}>Add a medication not on the list</button>}
      {adding && <AddForm ctx={ctx} onCancel={() => setAdding(false)} onDone={(v: V) => { setAdding(false); onDone(v); }} />}
    </section>
  );
}

function AddForm<V>({ ctx, onDone, onCancel }: { ctx: AddedContext; onDone: (v: V) => void; onCancel: () => void }) {
  const [photos, setPhotos] = useState<DocView[]>([]);
  const [uploading, setUploading] = useState(0);
  const [l, setL] = useState<Label>({ name: "", strength: "", form: "", dose: "", frequency: "", windows: [], route: "", isPrn: false, prnInstructions: "", controlled: false, prescriber: "", pharmacy: "", rxNumber: "" });
  const [status, setStatus] = useState("Received");
  const [exceptions, setExceptions] = useState<string[]>([]);
  const [quantity, setQuantity] = useState("");
  const [vEmail, setVEmail] = useState("");
  const [vPin, setVPin] = useState("");
  const [notes, setNotes] = useState("");
  const [att, setAtt] = useState({ name: "", relationship: ctx.adult ? "Self" : "Parent", phone: "", agreed: false });
  const [st, setSt] = useState<StatementView>({ started: "", reaction: "", reactionDetail: "", newAllergy: "", newAllergyDetail: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (p: Partial<Label>) => setL({ ...l, ...p });
  const held = status === "Received" || status === "ReceivedWithException";
  const id = (k: string) => `add-${k}`;
  const toggle = (list: string[], x: string) => (list.includes(x) ? list.filter((y) => y !== x) : [...list, x]);
  const { problems, check } = useProblems(addFormRules({ photos: photos.length, label: l, status, exceptions, quantity, vEmail, vPin, att, st }));
  const P = ({ k }: { k: string }) => <FieldProblem id={id(k)} problems={problems} />;
  const bad = (k: string) => invalid(id(k), problems);

  async function addPhotos(files: FileList | null, capture: "camera" | "upload") {
    if (!files) return;
    setError(null);
    for (const f of Array.from(files)) {
      setUploading((n) => n + 1);
      const r = await upload<{ photo: DocView }>(`/api/station/${encodeURIComponent(ctx.personKey)}/label-photos?capture=${capture}&name=${encodeURIComponent(f.name)}`, f);
      setUploading((n) => n - 1);
      if (r.ok) setPhotos((p) => [...p, r.data.photo]);
      else setError(r.data.error ?? "The photo didn't upload.");
    }
  }

  async function save() {
    setError(null);
    if (!check()) return;
    setBusy(true);
    const r = await api<{ visit: V }>("POST", `/api/station/${encodeURIComponent(ctx.personKey)}/medications/add`, {
      ...l,
      photoDocKeys: photos.map((p) => p.docKey),
      receivedStatus: status,
      exceptions: status === "ReceivedWithException" ? exceptions : [],
      quantity: held ? quantity : "",
      ...(l.controlled && held ? { verifier: { email: vEmail, pin: vPin } } : {}),
      attestation: { ...att, statement: st },
      notes,
    });
    setBusy(false);
    if (r.ok) onDone(r.data.visit);
    else setError(r.data.error ?? "That didn't save.");
  }

  return (
    <div className="mt-3 rounded-md border border-program/40 p-3">
      <h4 className="font-semibold">Add a medication not on the list</h4>
      <p className="text-xs text-slate-500">Recorded as {SOURCE_LABEL[ctx.addedSource] ?? ctx.addedSource}. It is held, not given, until it is authorized.</p>

      <fieldset className="mt-3" id={id("photos")}>
        <legend className="text-sm font-semibold">1. Photo of the label (at least one)</legend>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <label className="btn-primary cursor-pointer">
            Take a photo
            <input type="file" accept="image/*" capture="environment" multiple className="sr-only" onChange={(e) => { void addPhotos(e.target.files, "camera"); e.target.value = ""; }} />
          </label>
          <label className="btn-secondary cursor-pointer">
            Choose a file
            <input type="file" accept="image/*" multiple className="sr-only" onChange={(e) => { void addPhotos(e.target.files, "upload"); e.target.value = ""; }} />
          </label>
          {uploading > 0 && <span className="text-sm text-slate-600">Uploading…</span>}
        </div>
        <div className="mt-2 flex flex-wrap gap-2">{photos.map((p) => <Thumb key={p.docKey} doc={p} />)}</div>
        <p className="mt-1 text-xs text-slate-500">Photos only (JPEG, PNG, WebP or HEIC), 10 MB each.</p>
        <P k="photos" />
      </fieldset>

      <fieldset className="mt-3">
        <legend className="text-sm font-semibold">2. What the label says</legend>
        <div className="mt-1 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <div><label htmlFor={id("n")} className="field-label">Medication</label><input id={id("n")} className="field-input" {...bad("n")} value={l.name} onChange={(e) => set({ name: e.target.value })} /><P k="n" /></div>
          <div><label htmlFor={id("s")} className="field-label">Strength</label><input id={id("s")} className="field-input" value={l.strength} onChange={(e) => set({ strength: e.target.value })} /></div>
          <div><label htmlFor={id("f")} className="field-label">Form</label><input id={id("f")} className="field-input" placeholder="tablet, spray…" value={l.form} onChange={(e) => set({ form: e.target.value })} /></div>
          <div><label htmlFor={id("d")} className="field-label">Dose</label><input id={id("d")} className="field-input" {...bad("d")} value={l.dose} onChange={(e) => set({ dose: e.target.value })} /><P k="d" /></div>
          <div>
            <label htmlFor={id("r")} className="field-label">Route</label>
            <input id={id("r")} className="field-input" {...bad("r")} list={id("routes")} value={l.route} onChange={(e) => set({ route: e.target.value })} />
            <datalist id={id("routes")}>{ctx.routes.map((r) => <option key={r} value={r} />)}</datalist>
            <P k="r" />
          </div>
          <div><label htmlFor={id("fr")} className="field-label">How often{l.isPrn ? " (optional)" : ""}</label><input id={id("fr")} className="field-input" {...bad("fr")} value={l.frequency} onChange={(e) => set({ frequency: e.target.value })} /><P k="fr" /></div>
          <div><label htmlFor={id("pr")} className="field-label">Prescriber on the label</label><input id={id("pr")} className="field-input" value={l.prescriber} onChange={(e) => set({ prescriber: e.target.value })} /></div>
          <div><label htmlFor={id("ph")} className="field-label">Pharmacy</label><input id={id("ph")} className="field-input" value={l.pharmacy} onChange={(e) => set({ pharmacy: e.target.value })} /></div>
          <div><label htmlFor={id("rx")} className="field-label">Rx number</label><input id={id("rx")} className="field-input" value={l.rxNumber} onChange={(e) => set({ rxNumber: e.target.value })} /></div>
        </div>
        <label className="mt-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={l.isPrn} onChange={(e) => set({ isPrn: e.target.checked })} />As needed (PRN)</label>
        {l.isPrn && <div className="mt-1"><label htmlFor={id("prn")} className="field-label">When to give it</label><input id={id("prn")} className="field-input" {...bad("prn")} value={l.prnInstructions} onChange={(e) => set({ prnInstructions: e.target.value })} /><P k="prn" /></div>}
        <p className="field-label mt-2">Windows it seems to need (the provider decides)</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {ctx.windows.map((w) => <label key={w.code} className="flex items-center gap-1.5"><input type="checkbox" checked={l.windows.includes(w.code)} onChange={() => set({ windows: toggle(l.windows, w.code) })} />{w.name} ({w.startTime}–{w.endTime})</label>)}
        </div>
        <label className="mt-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={l.controlled} onChange={(e) => set({ controlled: e.target.checked })} />Controlled medication</label>
      </fieldset>

      <fieldset className="mt-3">
        <legend className="text-sm font-semibold">3. Receipt</legend>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {[["Received", "Received"], ["ReceivedWithException", "Received with an exception"], ["OnPerson", "On person (self-carry)"]].map(([k, t]) => (
            <label key={k} className="flex items-center gap-1.5"><input type="radio" name={id("st")} checked={status === k} onChange={() => setStatus(k)} />{t}</label>
          ))}
        </div>
        {status === "ReceivedWithException" && (
          <>
            <div id={id("x")} className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
              {ctx.exceptionCodes.map((x) => <label key={x} className="flex items-center gap-1.5"><input type="checkbox" checked={exceptions.includes(x)} onChange={() => setExceptions(toggle(exceptions, x))} />{x}</label>)}
            </div>
            <P k="x" />
          </>
        )}
        {held && <div className="mt-2 max-w-xs"><label htmlFor={id("q")} className="field-label">Quantity counted</label><input id={id("q")} className="field-input" {...bad("q")} inputMode="numeric" value={quantity} onChange={(e) => setQuantity(e.target.value.replace(/\D/g, ""))} /><P k="q" /></div>}
        {l.controlled && held && (
          <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3">
            <p className="text-sm font-medium text-amber-950">Second person: count it too, then sign with your own email and PIN on this device.</p>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <div><label htmlFor={id("ve")} className="field-label">Second person's email</label><input id={id("ve")} className="field-input" {...bad("ve")} type="email" autoComplete="off" value={vEmail} onChange={(e) => setVEmail(e.target.value)} /><P k="ve" /></div>
              <div><label htmlFor={id("vp")} className="field-label">Their PIN</label><input id={id("vp")} className="field-input" {...bad("vp")} type="password" inputMode="numeric" autoComplete="off" maxLength={4} value={vPin} onChange={(e) => setVPin(e.target.value.replace(/\D/g, ""))} /><P k="vp" /></div>
            </div>
          </div>
        )}
      </fieldset>

      <fieldset className="mt-3">
        <legend className="text-sm font-semibold">4. Attestation</legend>
        <div className="mt-1 grid gap-2 sm:grid-cols-3">
          <div>
            <label htmlFor={id("ar")} className="field-label">Who</label>
            <select id={id("ar")} className="field-input" value={att.relationship} onChange={(e) => setAtt({ ...att, relationship: e.target.value })}>
              <option value="Parent">Parent</option>
              <option value="Guardian">Guardian</option>
              <option value="Other">Other</option>
              {ctx.adult && <option value="Self">The attendee (18 or older)</option>}
            </select>
          </div>
          <div><label htmlFor={id("an")} className="field-label">Full name, typed</label><input id={id("an")} className="field-input" {...bad("an")} value={att.name} onChange={(e) => setAtt({ ...att, name: e.target.value })} /><P k="an" /></div>
          <div><label htmlFor={id("ap")} className="field-label">Phone{att.relationship === "Self" ? " (optional)" : ""}</label><input id={id("ap")} className="field-input" {...bad("ap")} type="tel" value={att.phone} onChange={(e) => setAtt({ ...att, phone: e.target.value })} /><P k="ap" /></div>
        </div>
        <div className="mt-3 space-y-2 text-sm">
          <div role="radiogroup" aria-labelledby={id("q1")}>
            <p id={id("q1")} className="field-label">How long has the student been taking this? <span className="text-red-600">*</span></p>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {(["New", "Established", "NotSure"] as const).map((k) => <label key={k} className="flex items-center gap-1.5"><input type="radio" name={id("q1r")} checked={st.started === k} onChange={() => setSt({ ...st, started: k })} />{STARTED_LABEL[k]}</label>)}
            </div>
            <P k="q1" />
          </div>
          <div role="radiogroup" aria-labelledby={id("q2")}>
            <p id={id("q2")} className="field-label">Has the student had any reaction to it, or any problem taking it with their other medications? <span className="text-red-600">*</span></p>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {(["No", "Yes"] as const).map((k) => <label key={k} className="flex items-center gap-1.5"><input type="radio" name={id("q2r")} checked={st.reaction === k} onChange={() => setSt({ ...st, reaction: k })} />{k === "Yes" ? "Yes, describe" : "No"}</label>)}
            </div>
            <P k="q2" />
            {st.reaction === "Yes" && <div className="mt-1"><label htmlFor={id("q2d")} className="field-label">Describe the reaction or problem <span className="text-red-600">*</span></label><textarea id={id("q2d")} className="field-input min-h-12" {...bad("q2d")} maxLength={500} value={st.reactionDetail} onChange={(e) => setSt({ ...st, reactionDetail: e.target.value })} /><P k="q2d" /></div>}
          </div>
          <div role="radiogroup" aria-labelledby={id("q3")}>
            <p id={id("q3")} className="field-label">Any medication allergies not already on the application? <span className="text-red-600">*</span></p>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {(["No", "Yes"] as const).map((k) => <label key={k} className="flex items-center gap-1.5"><input type="radio" name={id("q3r")} checked={st.newAllergy === k} onChange={() => setSt({ ...st, newAllergy: k })} />{k === "Yes" ? "Yes, list" : "No"}</label>)}
            </div>
            <P k="q3" />
            {st.newAllergy === "Yes" && <div className="mt-1"><label htmlFor={id("q3d")} className="field-label">List the medication allergies <span className="text-red-600">*</span></label><textarea id={id("q3d")} className="field-input min-h-12" {...bad("q3d")} maxLength={500} value={st.newAllergyDetail} onChange={(e) => setSt({ ...st, newAllergyDetail: e.target.value })} /><P k="q3d" /></div>}
          </div>
        </div>
        <label className="mt-2 flex items-start gap-2 text-sm">
          <input id={id("agreed")} type="checkbox" className="mt-1" {...bad("agreed")} checked={att.agreed} onChange={(e) => setAtt({ ...att, agreed: e.target.checked })} />
          <span>“{att.relationship === "Self" ? ctx.selfAttestationText : ctx.attestationText}.” You are the witness.</span>
        </label>
        <P k="agreed" />
      </fieldset>

      <label htmlFor={id("no")} className="field-label mt-3">Notes (optional)</label>
      <textarea id={id("no")} className="field-input min-h-16" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} />
      {error && <p className="field-error" role="alert">{error}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className="btn-primary" disabled={busy || uploading > 0} onClick={save}>Save as held (pending authorization)</button>
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
      {photos.length === 0 && <p className="mt-1 text-xs text-slate-500">Take at least one photo of the label first.</p>}
    </div>
  );
}
